import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    errorRedirect,
    IDisposable,
    InfiniteScroll,
    inject,
    Loader,
    NothingHere,
    SortOptions,
    SubTopBar,
    TasksStore,
    Title, toArray,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowClockwise } from '@react-icons/all-files/bs/BsArrowClockwise';
import { BsArrowLeftShort } from '@react-icons/all-files/bs/BsArrowLeftShort';
import { BsChevronDoubleUp } from '@react-icons/all-files/bs/BsChevronDoubleUp';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledButtonDropdown } from 'reactstrap';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { ManifestsStore } from './ManifestsStore';
import { ManifestDisplayItem } from './ManifestDisplayItem';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { defaultManifestColumns } from '../Account/AccountStore';
import { ActiveDisplay } from '../Displays/ActiveDisplay';
import { ManifestUnitItem } from './ManifestUnitItem';
import { uniq } from 'lodash';
import { BsCaretUpFill } from '@react-icons/all-files/bs/BsCaretUpFill';
import { BsCaretDownFill } from '@react-icons/all-files/bs/BsCaretDownFill';
import { BsArrowUpShort } from '@react-icons/all-files/bs/BsArrowUpShort';
import { BsArrowDownShort } from '@react-icons/all-files/bs/BsArrowDownShort';
import { BsWifi, BsWifiOff } from 'react-icons/bs';
import { FileSystemsStore } from './FileSystemsStore';
import cronstrue from "cronstrue";

export interface IUnitMeta {
    unitSerialNumber: string;
    displays: ActiveDisplay[];
}

export interface IManifestStatusParams {
    guid: string;
}

export interface IManifestStatusProps extends RouteComponentProps<IManifestStatusParams> {}

@autoinject
@observer
export class ManifestStatus extends React.Component<IManifestStatusProps> {
    public constructor(props: IManifestStatusProps) {
        super(props);

        this.vm = new DisplayListViewModel({
            columns: defaultManifestColumns,
            pageSize: 500,
        });
    }

    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.manifestsStore.loadAsync(this.props.match.params.guid);
            await this.manifestsStore.loadStatusAsync(this.props.match.params.guid);
            
            if(this.manifest && this.manifest.fileSystems){
                for(let fileSystem of this.manifest.fileSystems){
                    await this.fileSystemsStore.loadAsync(fileSystem);
                }
            }
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }

        try {
            this.handle = await this.manifestsStore.connectionManager.connect();
            await this.manifestsStore.subscribeToManifestAsync(this.props.match.params.guid);
        } catch (e: any) {
            console.error('SubscribeToManifest', e);
        }

        if (this.manifest) {
            this.vm.displayFilter = this.manifest.displayFilter;
        }
        this.vm.displayFilter.includeSiblings = true;
        this.vm.loadMoreAsync();
        this.sortStatus = SortOptions.Ascending;
    }

    public async componentWillUnmount() {
        this.handle?.dispose();
    }

    public render() {
        const { hasMore, busy, loadMoreAsync } = this.vm;
        return (
            <div>
                <Title>
                    <h3>{this.manifest?.name ?? 'Manifest'} Status</h3>
                    
                    {this.manifest &&
                        <div className="ms-2 d-inline-block">
                            <div>
                                Scheduled: {this.manifest.schedule ? cronstrue.toString(this.manifest.schedule) : ''}
                            </div>
                        </div>
                    }
                </Title>

                <SubTopBar>
                    {this.status && (
                        <div className="row justify-content-between text-center w-100">
                            <div className="col-3 col-md-2">
                                <LinkContainer exact to="/software-updates/manifests">
                                    <a className="btn btn-link">
                                    <BsArrowLeftShort style={{fontSize: 25}}/> Back
                                    </a>
                                </LinkContainer>
                            </div>
                            <div className="d-none d-md-block col-md-2">
                                <div className="flex-items-center h-100">
                                    <div className="text-center w-100">
                                        <strong>Status</strong>
                                        <div className={this.updatingStatusClass}>{this.updatingStatus}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-3 col-md-2">
                                <div className="flex-items-center h-100">
                                    <div className="text-center w-100">
                                        <strong>Updating</strong>
                                        <div>
                                            {this.status.updating}
                                            {this.updating && (
                                                <Tooltip message="Updates in progress">
                                                    <BsChevronDoubleUp className="text-running text-pulse ps-2" />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-3 col-md-2">
                                <div className="flex-items-center h-100">
                                    <div className="text-center w-100">
                                        <strong>Updated</strong>
                                        <div>
                                            {this.status.updated}/{this.status.total}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-3 col-md-2">
                                <div className="flex-items-center h-100">
                                    <div className="text-center w-100">
                                        <strong>Errors</strong>
                                        <div className="d-flex justify-content-center">
                                            <div className="ps-4 ms-4">{this.status.errored}</div>

                                            {this.showClear && (
                                                <div className="d-inline-block">
                                                    <Tooltip message="Reschedule Failed Displays">
                                                        <button
                                                            onClick={this.clearUpdateStatus}
                                                            className="btn btn-link py-0"
                                                        >
                                                            <BsArrowClockwise style={{ fontSize: 18 }} className="" />
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-none d-md-block col-md-2">
                                <div className="flex-items-center h-100">
                                    <div className="text-center w-100">
                                        <strong>Connection Issues</strong>
                                        <div className="d-flex justify-content-center">
                                            <div className="ps-4 ms-4">{this.status.noConnections}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </SubTopBar>
                
                <SubTopBar>
                    {this.manifest &&
                        <div>
                            Files: {this.fileSystems && this.fileSystems.map(f => <span>{f}</span>)}
                        </div>
                    }
                </SubTopBar>

                <SubTopBar className="d-none d-md-flex">
                    <div className="container-fluid px-5 border-top pt-2">
                        <div className="row">
                            <div className="col-1">Serial Number</div>
                            <div className="col-2">
                                <UncontrolledButtonDropdown>
                                    <DropdownToggle nav className="p-0">
                                        Status
                                        {this.statusIcon}
                                    </DropdownToggle>
                                    <DropdownMenu end className="p-0 dropdown-padding header-options-dropdown">
                                        <DropdownItem
                                            onClick={() => this.setStatusFilter(true)}
                                            className={this.statusButtonClass(true)}
                                        >
                                            Hide Offline Units
                                        </DropdownItem>

                                        <DropdownItem
                                            onClick={() => this.setStatusFilter(false)}
                                            className={this.statusButtonClass(false)}
                                        >
                                            Show Offline Units
                                        </DropdownItem>
                                    </DropdownMenu>
                                </UncontrolledButtonDropdown>
                            </div>
                            <div className="col-1">Last Poll</div>
                            <div className="col-2">Message</div>
                            <div className="col-1">Files</div>
                            <div className="col-1">MRI</div>
                            <div className="col-2">
                                <UncontrolledButtonDropdown>
                                    <DropdownToggle nav className="p-0">
                                        Update Status
                                        {this.sortArrow}
                                    </DropdownToggle>
                                    <DropdownMenu end className="p-0 dropdown-padding header-options-dropdown">
                                        <DropdownItem
                                            onClick={() => this.setSortStatus(SortOptions.Ascending)}
                                            className={this.buttonClass(SortOptions.Ascending)}
                                        >
                                            <BsArrowUpShort className="me-2" />
                                            Sort Ascending
                                        </DropdownItem>

                                        <DropdownItem
                                            onClick={() => this.setSortStatus(SortOptions.Descending)}
                                            className={this.buttonClass(SortOptions.Descending)}
                                        >
                                            <BsArrowDownShort className="me-2" />
                                            Sort Descending
                                        </DropdownItem>

                                        <DropdownItem
                                            onClick={() => this.setSortStatus(SortOptions.Unsort)}
                                            className={this.buttonClass(SortOptions.Unsort)}
                                        >
                                            Unsort
                                        </DropdownItem>
                                    </DropdownMenu>
                                </UncontrolledButtonDropdown>
                            </div>
                            <div className="col-2 d-flex">
                                <div className="ms-auto">
                                    <span className="pe-4">Actions</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </SubTopBar>

                <div className="container-fluid pt-3">
                    <div className="row" ref={this.setUnitUpdateStatusContainer}>
                        <InfiniteScroll
                            throttle={100}
                            threshold={300}
                            hasMore={hasMore}
                            isLoading={busy}
                            container={this.unitUpdateStatusContainer}
                            onLoadMore={loadMoreAsync}
                        >
                            {this.unitDisplays.map(u => (
                                <ManifestUnitItem
                                    unit={u}
                                    manifestGuid={this.manifest!.softwareUpdateManifestGuid}
                                    key={u.unitSerialNumber}
                                />
                            ))}
                            <NothingHere loading={this.loading} empty={!this.status?.displays.length} />
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private setSortStatus(order: SortOptions) {
        this.sortStatus = order;
    }

    @bind
    @action
    private setStatusFilter(filter: boolean) {
        this.hideOfflineUnits = filter;
    }

    @bind
    @action
    private setUnitUpdateStatusContainer(r: HTMLDivElement | null) {
        this.unitUpdateStatusContainer = r;
    }

    @bind
    private async clearUpdateStatus() {
        const task = this.tasksStore.startSessionTask(`Clear All Update Status`);

        try {
            await this.manifestsStore.clearAsync(this.props.match.params.guid);

            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @computed
    private get manifest() {
        return this.manifestsStore.manifests.get(this.props.match.params.guid);
    }

    @computed
    private get updatingStatus() {
        if (this.status == null) return 'Idle';
        else if (this.status.updated == this.status.total) return 'Up to Date';
        else if (this.status.updating > 0) return 'Updating';
        else if (this.status.scheduled > 0) return 'Scheduled';
        else if (this.status.errored > 0 && this.status.idle > 0 && this.manifest?.active)
            return 'Updating with Errors';
        else if (this.status.errored > 0) return 'Idle with Errors';
        else if (this.status.idle > 0 && this.manifest?.active) return 'Scheduling';
        else if (this.status.idle > 0) return 'Idle';
        else return '-';
    }

    @computed
    private get updatingStatusClass() {
        if (this.status == null) return '';
        else if (this.status.updated == this.status.total) return 'text-success';
        else if (this.status.updating > 0) return 'text-info';
        else if (this.status.scheduled > 0) return 'text-info';
        else if (this.status.errored > 0 && this.status.idle > 0 && this.manifest?.active) return 'text-warning';
        else if (this.status.errored > 0) return 'text-danger';
        else if (this.status.idle > 0 && this.manifest?.active) return 'text-info';
        else if (this.status.idle > 0) return '';
        else return '';
    }

    @computed
    private get status() {
        return this.manifestsStore.manifestStatuses.get(this.props.match.params.guid);
    }

    @computed
    private get unitDisplays(): IUnitMeta[] {
        const filteredDisplays = this.hideOfflineUnits
            ? this.vm.displays.filter(d => d.displayStatus == Mri.DisplayStatuses.DisplayStatus.Online)
            : this.vm.displays;
        const groupedDisplays = uniq(
            filteredDisplays.map(d => {
                return d.unitSerialNumber ? d.unitSerialNumber : d.serialNumber;
            })
        );

        let units: IUnitMeta[] = [];
        units = groupedDisplays.map(s => {
            const displays = this.vm.displays.filter(d => d.unitSerialNumber == s || d.serialNumber == s);
            return {
                unitSerialNumber: s,
                displays,
            };
        });

        if (this.sortStatus == SortOptions.Unsort) {
            return units;
        }

        units = units.sort((a, b) => {
            let aPriority = 0;
            let bPriority = 0;
            // A units
            const updatedAUnits = a.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 7);
            const updatingAUnits = a.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 2);
            const updatingSlaveAUnits = a.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 3);
            const pendingAUnits = a.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 1);
            const scheduledAUnits = a.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 6);
            const idleAUnits = a.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 0);
            const noConnectionAUnits = a.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 5);
            const errorAUnits = a.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 4);
            // B units
            const updatedBUnits = b.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 7);
            const updatingBUnits = b.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 2);
            const updatingSlaveBUnits = b.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 3);
            const pendingBUnits = b.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 1);
            const scheduledBUnits = b.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 6);
            const idleBUnits = b.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 0);
            const noConnectionBUnits = b.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 5);
            const errorBUnits = b.displays.filter(d => d.displaySoftwareUpdateStatus?.state == 4);

            aPriority =
                updatedAUnits.length * 1 +
                updatingAUnits.length * 2 +
                updatingSlaveAUnits.length * 3 +
                pendingAUnits.length * 4 +
                scheduledAUnits.length * 5 +
                idleAUnits.length * 6 +
                noConnectionAUnits.length * 7 +
                errorAUnits.length * 8;

            bPriority =
                updatedBUnits.length * 1 +
                updatingBUnits.length * 2 +
                updatingSlaveBUnits.length * 3 +
                pendingBUnits.length * 4 +
                scheduledBUnits.length * 5 +
                idleBUnits.length * 6 +
                noConnectionBUnits.length * 7 +
                errorBUnits.length * 8;

            if (this.sortStatus == SortOptions.Ascending) {
                return bPriority - aPriority;
            }

            return aPriority - bPriority;
        });

        return units;
    }

    @computed
    private get updating() {
        return !!this.status && this.status.updating > 0;
    }

    @computed
    private get showClear() {
        return !!this.status?.errored;
    }

    @computed
    private get sortArrow() {
        if (this.sortStatus == SortOptions.Ascending) {
            return <BsCaretUpFill className="ms-1" />;
        } else if (this.sortStatus == SortOptions.Descending) {
            return <BsCaretDownFill className="ms-1" />;
        }
    }

    @computed
    private get statusIcon() {
        if (this.hideOfflineUnits) {
            return ' (Online Units)';
        } else {
            return ' (All Units)';
        }
    }

    @bind
    private buttonClass(order: SortOptions) {
        let className = 'text-header fw-bold py-0';

        if (this.sortStatus == order) className += ' active';

        return className;
    }

    @bind
    private statusButtonClass(filter: boolean) {
        let className = 'text-header fw-bold py-0';

        if (this.hideOfflineUnits == filter) className += ' active';

        return className;
    }
    
    @computed
    private get fileSystems(){
        if(!this.manifest) return [];
        return this.manifest.fileSystems.map(f => {
            const fS = this.fileSystemsStore.fileSystems.get(f);
            if (fS) return fS.fileName;
        });
    }

    @observable private loading = false;
    @observable private hideOfflineUnits = false;
    @observable
    private sortStatus: SortOptions = SortOptions.Unsort;
    private handle?: IDisposable;
    private unitUpdateStatusContainer: HTMLDivElement | null = null;
    private vm!: DisplayListViewModel;
    @inject
    private readonly manifestsStore!: ManifestsStore;
    @inject
    private readonly fileSystemsStore!: FileSystemsStore;
    @inject
    private readonly displaysStore!: DisplaysStore;
    @inject
    private readonly tasksStore!: TasksStore;
}
